import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { fulfillmentsApi } from '../../lib/api';
import Navbar from '../Navbar';

interface Party {
  id: string;
  name: string;
  role: 'renter' | 'owner';
  hasConfirmed: boolean;
  confirmedAt?: string;
}

export default function DigitalHandshake() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [parties, setParties] = useState<Party[]>([
    { id: 'renter', name: 'Renter', role: 'renter', hasConfirmed: false },
    { id: 'owner', name: 'Owner', role: 'owner', hasConfirmed: false },
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'renter' | 'owner' | null>(null);
  const [rentalId, setRentalId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    fetchFulfillment();
  }, [id]);

  // The other party can confirm their side at any point while this screen is open --
  // without polling, seeing that happen required a manual reload. Stops once this
  // fulfillment reaches a terminal state, matching PickupTracker.tsx's same pattern.
  useEffect(() => {
    if (!id || completed) return;
    const interval = setInterval(() => fetchFulfillment(true), 10000);
    return () => clearInterval(interval);
  }, [id, completed]);

  const fetchFulfillment = async (isBackgroundRefresh = false) => {
    try {
      const response = await fulfillmentsApi.get(id!);
      if (response.data.success) {
        const fulfillment = response.data.data;
        setRentalId(fulfillment.rental?.id ?? null);
        // Determine user role from auth context
        if (user) {
          const role = fulfillment.rental?.renter?.id === user.id ? 'renter'
            : fulfillment.rental?.owner?.id === user.id ? 'owner' : null;
          setUserRole(role);
        }
        setParties([
          {
            id: fulfillment.rental?.renter?.id || 'renter',
            name: `${fulfillment.rental?.renter?.firstName} ${fulfillment.rental?.renter?.lastName}` || 'Renter',
            role: 'renter',
            hasConfirmed: fulfillment.pickupDetails?.renterVerifiedAt != null,
            confirmedAt: fulfillment.pickupDetails?.renterVerifiedAt ?? undefined,
          },
          {
            id: fulfillment.rental?.owner?.id || 'owner',
            name: `${fulfillment.rental?.owner?.firstName} ${fulfillment.rental?.owner?.lastName}` || 'Owner',
            role: 'owner',
            hasConfirmed: fulfillment.pickupDetails?.lenderVerifiedAt != null,
            confirmedAt: fulfillment.pickupDetails?.lenderVerifiedAt ?? undefined,
          },
        ]);

        if (fulfillment.status === 'PICKED_UP' || fulfillment.status === 'COMPLETED') {
          setCompleted(true);
        }
      }
    } catch (err: any) {
      if (!isBackgroundRefresh) setError(err?.response?.data?.error || 'Failed to load pickup details');
    } finally {
      if (!isBackgroundRefresh) setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Real two-party gate: this only records the caller's own confirmation and only
      // transitions the fulfillment once both parties have confirmed -- see the backend
      // route's comment for why this replaced a direct PATCH .../status call.
      await fulfillmentsApi.confirmHandshake(id!);
      await fetchFulfillment();
    } catch (err: any) {
      if (err?.response?.data?.code === 'PRE_PICKUP_PHOTOS_REQUIRED') {
        navigate(`/fulfillment/${id}/inspect`);
        return;
      }
      setError(err?.response?.data?.error || 'Failed to confirm pickup');
    } finally {
      setSubmitting(false);
    }
  };

  const bothConfirmed = parties.every(p => p.hasConfirmed);
  const userConfirmed = parties.find(p => p.role === userRole)?.hasConfirmed;

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-[var(--text-secondary)]">Loading...</div>
        </div>
      </div>
    );
  }

  if (completed || bothConfirmed) {
    return (
      <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="bg-white rounded-xl border border-[var(--border)] p-8 text-center max-w-md w-full">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="mdi mdi-handshake text-4xl text-green-600"></i>
            </div>
            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">Pickup Complete! 🎉</h2>
            <p className="text-[var(--text-secondary)] mb-6">
              Both parties have confirmed the equipment pickup. The rental is now active.
            </p>

            <div className="flex flex-col gap-3">
              {rentalId && (
                <button
                  onClick={() => navigate(`/rental/${rentalId}`)}
                  className="btn btn-primary w-full"
                >
                  View Rental
                </button>
              )}
              <button
                onClick={() => navigate('/dashboard')}
                className="btn btn-secondary w-full"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      <Navbar />
      <div className="container mx-auto px-4 max-w-md py-6">
        {/* Header */}
        <div className="bg-white rounded-xl border border-[var(--border)] p-6 mb-4">
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                parties[0].hasConfirmed ? 'bg-green-100' : 'bg-amber-100'
              }`}>
                <i className={`mdi mdi-account text-2xl ${
                  parties[0].hasConfirmed ? 'text-green-600' : 'text-amber-600'
                }`}></i>
              </div>
              <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center ${
                parties[0].hasConfirmed ? 'bg-green-500' : 'bg-gray-300'
              }`}>
                <i className={`mdi ${parties[0].hasConfirmed ? 'mdi-check' : 'mdi-clock'} text-white text-xs`}></i>
              </div>
            </div>
            
            <div className="w-24 h-0.5 bg-[var(--border)] mx-4"></div>
            
            <div className="relative">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                parties[1].hasConfirmed ? 'bg-green-100' : 'bg-amber-100'
              }`}>
                <i className={`mdi mdi-account text-2xl ${
                  parties[1].hasConfirmed ? 'text-green-600' : 'text-amber-600'
                }`}></i>
              </div>
              <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center ${
                parties[1].hasConfirmed ? 'bg-green-500' : 'bg-gray-300'
              }`}>
                <i className={`mdi ${parties[1].hasConfirmed ? 'mdi-check' : 'mdi-clock'} text-white text-xs`}></i>
              </div>
            </div>
          </div>
          
          <h1 className="text-xl font-bold text-[var(--text-primary)] text-center">
            Confirm Pickup
          </h1>
          <p className="text-sm text-[var(--text-secondary)] text-center mt-2">
            Both parties must confirm to complete the pickup
          </p>
        </div>

        {/* Status Cards */}
        <div className="space-y-3 mb-6">
          {parties.map((party) => (
            <div
              key={party.id}
              className={`rounded-xl border p-4 ${
                party.hasConfirmed
                  ? 'bg-green-50 border-green-200'
                  : 'bg-white border-[var(--border)]'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  party.hasConfirmed ? 'bg-green-100' : 'bg-amber-100'
                }`}>
                  <i className={`mdi mdi-account text-lg ${
                    party.hasConfirmed ? 'text-green-600' : 'text-amber-600'
                  }`}></i>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-[var(--text-primary)]">
                    {party.name}
                  </p>
                  <p className={`text-sm ${
                    party.hasConfirmed ? 'text-green-600' : 'text-amber-600'
                  }`}>
                    {party.hasConfirmed
                      ? `Confirmed at ${new Date(party.confirmedAt!).toLocaleTimeString()}`
                      : 'Waiting for confirmation...'
                    }
                  </p>
                </div>
                <i className={`mdi text-xl ${
                  party.hasConfirmed ? 'mdi-check-circle text-green-500' : 'mdi-clock-outline text-amber-500'
                }`}></i>
              </div>
            </div>
          ))}
        </div>

        {/* User Action */}
        {!userConfirmed && userRole && (
          <div className="bg-white rounded-xl border border-[var(--border)] p-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            <p className="text-sm text-[var(--text-secondary)] mb-4">
              By confirming, you acknowledge that:
            </p>
            <ul className="text-sm text-[var(--text-secondary)] space-y-2 mb-6">
              <li className="flex items-start gap-2">
                <i className="mdi mdi-check text-[var(--forest-green)] mt-0.5"></i>
                <span>The equipment has been inspected</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="mdi mdi-check text-[var(--forest-green)] mt-0.5"></i>
                <span>Both parties agree on the condition</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="mdi mdi-check text-[var(--forest-green)] mt-0.5"></i>
                <span>The rental period begins now</span>
              </li>
            </ul>

            <button
              onClick={handleConfirm}
              disabled={submitting}
              className="btn btn-primary w-full"
            >
              {submitting ? (
                <>
                  <i className="mdi mdi-loading mdi-spin mr-2"></i>
                  Confirming...
                </>
              ) : (
                <>
                  <i className="mdi mdi-handshake mr-2"></i>
                  I Confirm Pickup
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
